import axios from 'axios';
import { shrinkIfHuge } from './imageResize';

const BASE = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_API_URL || 'http://localhost:4001')
  : '';

const http = axios.create({ baseURL: BASE, timeout: 15000 });

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// 后台隐蔽登录入口（与 App.js 的 ADMIN_LOGIN_PATH 保持一致）
const ADMIN_LOGIN_PATH = '/ops-9k27x';

http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.hash = ADMIN_LOGIN_PATH;
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login: (username, password) => http.post('/api/auth/login', { username, password }).then(r => r.data),
};

// 当前登录用户的角色/会话（角色信息登录时写入 localStorage）
export const auth = {
  role: () => localStorage.getItem('admin_role') || 'sub',
  isSuperadmin: () => (localStorage.getItem('admin_role') || 'sub') === 'superadmin',
  name: () => localStorage.getItem('admin_name') || '',
  // 功能权限位；null（超管）视为全部。存储为 JSON 字符串，'*' 代表全部。
  caps: () => {
    const raw = localStorage.getItem('admin_caps');
    if (raw === null || raw === '*') return null;
    try { return JSON.parse(raw); } catch { return []; }
  },
  hasCap: (cap) => {
    if ((localStorage.getItem('admin_role') || 'sub') === 'superadmin') return true;
    const raw = localStorage.getItem('admin_caps');
    if (raw === null || raw === '*') return true;
    try { return JSON.parse(raw).includes(cap); } catch { return false; }
  },
  save: ({ token, role, username, capabilities }) => {
    localStorage.setItem('admin_token', token);
    if (role) localStorage.setItem('admin_role', role);
    if (username) localStorage.setItem('admin_name', username);
    // capabilities === null / undefined（超管）存 '*'
    localStorage.setItem('admin_caps', capabilities == null ? '*' : JSON.stringify(capabilities));
  },
  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_name');
    localStorage.removeItem('admin_caps');
    window.location.hash = ADMIN_LOGIN_PATH;
  },
};

export const statsApi = {
  overview: () => http.get('/api/stats/overview').then(r => r.data),
};

export const userApi = {
  list:   ()         => http.get('/api/users').then(r => r.data),
  create: (data)     => http.post('/api/users', data).then(r => r.data),
  update: (id, data) => http.put(`/api/users/${id}`, data).then(r => r.data),
  delete: (id)       => http.delete(`/api/users/${id}`).then(r => r.data),
};

// 上传单独放宽超时：手机原图几 MB，走新加坡中转 15 秒经常不够
const UPLOAD_TIMEOUT = 120000;

export const uploadApi = {
  image: async (file) => {
    const form = new FormData();
    form.append('file', await shrinkIfHuge(file));
    return http.post('/api/upload', form, { timeout: UPLOAD_TIMEOUT }).then(r => r.data.url);
  },
  delete: (url) => http.delete('/api/upload', { data: { url } }).then(r => r.data),
};

export const merchantApi = {
  list:   ()           => http.get('/api/merchants').then(r => r.data),
  get:    (id)         => http.get(`/api/merchants/${id}`).then(r => r.data),
  search: (name)       => http.get('/api/merchants/search', { params: { name } }).then(r => r.data),
  add:    (data)       => http.post('/api/merchants', data).then(r => r.data),
  update: (id, data)   => http.put(`/api/merchants/${id}`, data).then(r => r.data),
  delete: (id)         => http.delete(`/api/merchants/${id}`).then(r => r.data),
};

export const campaignApi = {
  list: (merchantId) => http.get('/api/campaigns', { params: { merchantId } }).then(r => r.data),
  get:  (id) => http.get(`/api/campaigns/${id}`).then(r => r.data),
  create: (data) => http.post('/api/campaigns', data).then(r => r.data),
  update: (id, data) => http.put(`/api/campaigns/${id}`, data).then(r => r.data),
  delete: (id) => http.delete(`/api/campaigns/${id}`).then(r => r.data),
};

export const articleApi = {
  list:   (campaignId) => http.get(`/api/campaigns/${campaignId}/articles`).then(r => r.data),
  get:    (id) => http.get(`/api/articles/${id}`).then(r => r.data),
  create: (campaignId, data) => http.post(`/api/campaigns/${campaignId}/articles`, data).then(r => r.data),
  update: (id, data) => http.put(`/api/articles/${id}`, data).then(r => r.data),
  delete: (id) => http.delete(`/api/articles/${id}`).then(r => r.data),
  variant: (id) => http.post(`/api/articles/${id}/variant`).then(r => r.data),
  rewriteContent: (id) => http.post(`/api/articles/${id}/rewrite-content`).then(r => r.data),
  perturbImages:  (id) => http.post(`/api/articles/${id}/perturb-images`).then(r => r.data),
  translate:      (id) => http.post(`/api/articles/${id}/translate`).then(r => r.data),
  // 用券宝宝内部账号发到券宝宝：channel=post 探索帖子 / review 店铺评价
  publishQbb:     (id, d) => http.post(`/api/articles/${id}/publish-qbb`, d).then(r => r.data),
};

// admin：券宝宝内部账号池（仅超管）
export const qbbAccountApi = {
  list:   ()         => http.get('/api/qbb-accounts').then(r => r.data),
  create: (data)     => http.post('/api/qbb-accounts', data).then(r => r.data),
  update: (id, data) => http.put(`/api/qbb-accounts/${id}`, data).then(r => r.data),
  login:  (id)       => http.post(`/api/qbb-accounts/${id}/login`).then(r => r.data),
  delete: (id)       => http.delete(`/api/qbb-accounts/${id}`).then(r => r.data),
};

// ── 博主端（H5 投稿）──────────────────────────────
// 与后台完全独立：用 blogger_token，不触发后台登录跳转。
const bhttp = axios.create({ baseURL: BASE, timeout: 20000 });
bhttp.interceptors.request.use((config) => {
  const token = localStorage.getItem('blogger_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

export const bloggerAuth = {
  token:  () => localStorage.getItem('blogger_token') || '',
  phone:  () => localStorage.getItem('blogger_phone') || '',
  save:   ({ token, phone }) => {
    localStorage.setItem('blogger_token', token);
    if (phone) localStorage.setItem('blogger_phone', phone);
  },
  logout: () => {
    localStorage.removeItem('blogger_token');
    localStorage.removeItem('blogger_phone');
  },
};

// App 里用 webview 打开我们的 H5 时，token 通过 ?t= 带进来，读一次就存下、并从地址栏抹掉。
// 这样博主在 App 里点悬浮入口就直接进来了，不用再登录一次。
export const adoptTokenFromUrl = () => {
  try {
    const hash = window.location.hash || '';
    const qi = hash.indexOf('?');
    if (qi < 0) return;
    const q = new URLSearchParams(hash.slice(qi + 1));
    const t = q.get('t');
    if (!t) return;
    localStorage.setItem('blogger_token', t);
    const phone = q.get('phone');
    if (phone) localStorage.setItem('blogger_phone', phone);
    q.delete('t'); q.delete('phone');
    const rest = q.toString();
    window.location.hash = hash.slice(0, qi) + (rest ? `?${rest}` : '');
  } catch { /* 地址栏解析失败不影响正常使用 */ }
};

export const bloggerApi = {
  merchant:   (id)          => bhttp.get(`/api/blogger/merchant/${id}`).then(r => r.data),
  universal:  ()            => bhttp.get('/api/blogger/universal').then(r => r.data),
  categories: ()            => bhttp.get('/api/blogger/categories').then(r => r.data),
  sendCode:   (phone)       => bhttp.post('/api/blogger/auth/send-code', { phone }).then(r => r.data),
  login:      (phone, password) => bhttp.post('/api/blogger/auth/login', { account: phone, password }).then(r => r.data),
  register:   (phone, password, confirmPassword, code) => bhttp.post('/api/blogger/auth/register', { phone, password, confirmPassword, code }).then(r => r.data),
  me:         ()            => bhttp.get('/api/blogger/me').then(r => r.data),
  saveProfile:(data)        => bhttp.post('/api/blogger/profile', data).then(r => r.data),
  upload:     async (file) => {
    const form = new FormData();
    form.append('file', await shrinkIfHuge(file));
    return bhttp.post('/api/blogger/upload', form, { timeout: UPLOAD_TIMEOUT }).then(r => r.data.url);
  },
  submit:     (data) => bhttp.post('/api/blogger/submit', data).then(r => r.data),
  getEnroll:  ()     => bhttp.get('/api/blogger/enroll').then(r => r.data),
  saveEnroll: (data) => bhttp.post('/api/blogger/enroll', data).then(r => r.data),
  // 探店任务
  tasks:       ()                          => bhttp.get('/api/blogger/tasks').then(r => r.data),
  applyTask:   (activityId, visitAt, visitPeopleCount, note) => bhttp.post(`/api/blogger/tasks/${activityId}/apply`, { visitAt, visitPeopleCount, note }).then(r => r.data),
  markVisited: (appId)                     => bhttp.post(`/api/blogger/tasks/app/${appId}/visited`).then(r => r.data),
  submitDraft: (appId, d)                  => bhttp.post(`/api/blogger/tasks/app/${appId}/draft`, d).then(r => r.data),
  publishTask: (appId, d)                  => bhttp.post(`/api/blogger/tasks/app/${appId}/publish`, d).then(r => r.data),
};

// admin：探店 / 博主管理
export const tandianApi = {
  bloggers:      (params)     => http.get('/api/tandian/bloggers', { params }).then(r => r.data),
  blogger:       (id)         => http.get(`/api/tandian/bloggers/${id}`).then(r => r.data),
  updateBlogger: (id, d)      => http.put(`/api/tandian/bloggers/${id}`, d).then(r => r.data),
  reviewBlogger: (id, action) => http.post(`/api/tandian/bloggers/${id}/review`, { action }).then(r => r.data),
  deleteBlogger: (id)         => http.delete(`/api/tandian/bloggers/${id}`).then(r => r.data),
  // 探店活动
  activities:     (merchantId) => http.get('/api/tandian/activities', { params: { merchantId } }).then(r => r.data),
  activity:       (id)         => http.get(`/api/tandian/activities/${id}`).then(r => r.data),
  createActivity: (d)          => http.post('/api/tandian/activities', d).then(r => r.data),
  updateActivity: (id, d)      => http.put(`/api/tandian/activities/${id}`, d).then(r => r.data),
  deleteActivity: (id)         => http.delete(`/api/tandian/activities/${id}`).then(r => r.data),
  addBlogger:     (id, bloggerProfileId) => http.post(`/api/tandian/activities/${id}/add-blogger`, { bloggerProfileId }).then(r => r.data),
  patchApp:       (appId, d)   => http.patch(`/api/tandian/applications/${appId}`, d).then(r => r.data),
};

// 博主入驻 H5 地址（后台生成邀请二维码用；有审批把关，可常驻）
export const bloggerEnrollUrl = () => `${window.location.origin}/#/blogger/enroll`;

// 商家分享页（公开，凭 token，不登录）
export const tshareApi = {
  get:          (token)              => axios.get(`${BASE}/api/tshare/${token}`).then(r => r.data),
  select:       (token, selectedIds) => axios.post(`${BASE}/api/tshare/${token}/select`, { selectedIds }).then(r => r.data),
  approveDraft: (token, appId)       => axios.post(`${BASE}/api/tshare/${token}/approve-draft`, { appId }).then(r => r.data),
  rejectDraft:  (token, appId, comment) => axios.post(`${BASE}/api/tshare/${token}/reject-draft`, { appId, comment }).then(r => r.data),
};
export const tshareUrl = (token, opts = {}) => {
  const q = new URLSearchParams();
  if (opts.view) q.set('view', opts.view);
  if (opts.app) q.set('app', opts.app);
  const qs = q.toString();
  return `${window.location.origin}/#/t/${token}${qs ? `?${qs}` : ''}`;
};

// admin：统一博主文章列表
export const submissionApi = {
  list:    (params)         => http.get('/api/submissions', { params }).then(r => r.data),
  get:     (id)             => http.get(`/api/submissions/${id}`).then(r => r.data),
  publish: (id, categoryId) => http.post(`/api/submissions/${id}/publish`, { categoryId }).then(r => r.data),
  categories: ()            => http.get('/api/blogger/categories').then(r => r.data),
  promote: (id, targetCampaignId) => http.post(`/api/submissions/${id}/promote`, { targetCampaignId }).then(r => r.data),
  archive: (id)             => http.post(`/api/submissions/${id}/archive`).then(r => r.data),
  delete:  (id)             => http.delete(`/api/submissions/${id}`).then(r => r.data),
};

// ── 撮合 Agent（MVP，后端 AGENT_MVP=true 时才存在）────────────────
// 审题面。**不走 admin token** —— 做这件事的人没有后台账号，
// 拿的是单独发给他的一个口令，口令即身份（后端按它认人）。
// 所以这里单开一个实例，绝不能把 admin_token 带过去。
const evalHttp = axios.create({ baseURL: BASE, timeout: 15000 });
export const evalApi = {
  key: () => sessionStorage.getItem('evalKey') || '',
  setKey: (k) => sessionStorage.setItem('evalKey', k),
  next: () => evalHttp.get('/api/agent/eval/next',
    { headers: { 'X-Eval-Key': evalApi.key() } }).then(r => r.data),
  review: (body) => evalHttp.post('/api/agent/eval/review', body,
    { headers: { 'X-Eval-Key': evalApi.key() } }).then(r => r.data),
};

export const agentApi = {
  // 审题结果只给超管看，不给做题的人看 —— 看了会被别人的判断带跑
  evalSummary:  ()            => http.get('/api/agent/admin/eval/summary').then(r => r.data),
  // 后台沙盘
  status:       ()            => http.get('/api/agent/admin/status').then(r => r.data),
  // 从 Google 查营业时间：只查不存，查到的填进输入框，点「保存」才落库
  previewHours: (id)          => http.post(`/api/agent/admin/merchants/${id}/fetch-hours`, { dryRun: true }).then(r => r.data),
  activities:   (params)      => http.get('/api/agent/admin/activities', { params }).then(r => r.data),
  // 店铺列表按**商家**列。activities 那条是按活动容器列的，
  // 开通了探店但还没谈起来的店在它里面查不到（容器是条件谈齐时才自建的）
  shops:        (params)      => http.get('/api/agent/admin/shops', { params }).then(r => r.data),
  // 全部待办：不分店不分活动，一次列完
  todo:         (params)      => http.get('/api/agent/admin/todo', { params }).then(r => r.data),
  // 手动跑一次调度。自动撮合没打开时，超时换人/补位/催到店/催发布/复查帖子
  // 这些都不会自己跑，演练时靠这个按钮推一轮
  sweep:        ()            => http.post('/api/agent/admin/sweep').then(r => r.data),
  // 把商家端的登录手机号绑到库里某家店上，绑完他那边就能用了
  linkShop:     (mobile, merchantId) => http.post('/api/agent/admin/shop-links', { mobile, merchantId }).then(r => r.data),
  // 灰度开关：这家店的商家端能不能看到「博主探店」
  enableShop:   (merchantId, enabled) => http.post(`/api/agent/admin/shops/${merchantId}/enable`, { enabled }).then(r => r.data),
  // 购买流水：哪家店买了什么。带 merchantId 就只看这家店（店铺详情页用）。
  // 名额在商家点购买那一下就发了，这里纯粹是账本
  subOrders:    (params)      => http.get('/api/agent/admin/sub-orders', { params }).then(r => r.data),
  // 线下收了钱，点一下发名额。**走的是跟支付回调同一个出口**，不会重复发
  markOrderPaid: (id, note) => http.post(`/api/agent/admin/sub-orders/${id}/paid`, { note }).then(r => r.data),
  // 退款。**会真的把钱退回去**，名额只收得回没用掉的那部分
  refundOrder:   (id, reason) => http.post(`/api/agent/admin/sub-orders/${id}/refund`, { reason }).then(r => r.data),
  // 线下收了钱/答应送几位时，运营手动加名额。count 可以是负数（加错了减回来）
  grantQuota:   (merchantId, count, note) => http.post(`/api/agent/admin/shops/${merchantId}/quota`, { count, note }).then(r => r.data),
  match:        (activityId, limit) => http.get(`/api/agent/admin/match/${activityId}`, { params: { limit } }).then(r => r.data),
  invite:       (activityId, phones) => http.post('/api/agent/admin/invite', { activityId, phones }).then(r => r.data),
  deals:        (params)      => http.get('/api/agent/admin/deals', { params }).then(r => r.data),
  commit:       (dealId)      => http.post(`/api/agent/admin/deals/${dealId}/commit`).then(r => r.data),
  // 一张单子的全貌：商家要什么 / 这单谈成什么 / 博主是谁 + 已经比对好的体检
  dealDetail:   (dealId)      => http.get(`/api/agent/admin/deals/${dealId}/detail`).then(r => r.data),
  conversations:()            => http.get('/api/agent/admin/conversations').then(r => r.data),
  messages:     (id)          => http.get(`/api/agent/admin/conversations/${id}/messages`).then(r => r.data),
  // 两种说话方式，别混：opsSay 是只给 agent 看的内部批注（对方看不到），
  // opsSend 是真的发给对方（以助理身份，并且推送过去）
  opsSay:       (id, text)    => http.post(`/api/agent/admin/conversations/${id}/say`, { text }).then(r => r.data),
  opsSend:      (id, text)    => http.post(`/api/agent/admin/conversations/${id}/send`, { text }).then(r => r.data),
  openMerchantConv: (dealId) => http.post(`/api/agent/admin/deals/${dealId}/merchant-conversation`).then(r => r.data),
  // 运营代博主回填发布链接 / 删一条。博主在微信里把链接甩过来、或者他就是不爱开 App，
  // 让运营替他贴一下比一直催有用
  addPost: (dealId, data) => http.post(`/api/agent/admin/deals/${dealId}/posts`, data).then(r => r.data),
  delPost: (dealId, idx) => http.delete(`/api/agent/admin/deals/${dealId}/posts/${idx}`).then(r => r.data),
  takeover:     (id, on)      => http.post(`/api/agent/admin/conversations/${id}/takeover`, { on }).then(r => r.data),
  kick:         (id)          => http.post(`/api/agent/admin/conversations/${id}/kick`).then(r => r.data),
  // 这条待办处理完了，把 needsOps 摘掉，别一直挂着
  handled:      (id, tag)     => http.post(`/api/agent/admin/conversations/${id}/handled`, { tag }).then(r => r.data),
  // 运营报问题：文字 + 截图 + 自动带上的现场（哪个页面、看的哪条单子）
  reportIssue:  (d)           => http.post('/api/agent/admin/feedback', d).then(r => r.data),
  feedback:     (status, page) => http.get('/api/agent/admin/feedback', { params: { status, page } }).then(r => r.data),
  judgeFeedback:(id, d)       => http.post(`/api/agent/admin/feedback/${id}`, d).then(r => r.data),
  terms:        (merchantId)  => http.get(`/api/agent/admin/terms/${merchantId}`).then(r => r.data),
  saveTerms:    (merchantId, d) => http.put(`/api/agent/admin/terms/${merchantId}`, d).then(r => r.data),
  signals:      (phone)       => http.get(`/api/agent/admin/signals/${phone}`).then(r => r.data),
  signalEdit:   (phone, field, value) => http.post(`/api/agent/admin/signals/${phone}/edit`, { field, value }).then(r => r.data),
  // 画像待审：助理学到的自由文本先落这儿，运营过一眼才进画像
  signalEvents: (params)      => http.get('/api/agent/admin/signal-events', { params }).then(r => r.data),
  // 体检页：两道闸 —— 后台登录（http 已带 token）+ 口令。
  // 口令走请求体不走 URL：URL 上的口令会留在浏览器历史、referer 和服务器日志里
  health:       (key)         => http.post('/api/agent/admin/health', { key }).then(r => r.data),
  // value：改了再进画像时带上改后的内容
  judgeSignal:  (id, action, tag, value) => http.post(`/api/agent/admin/signal-events/${id}/${action}`, { tag, value }).then(r => r.data),
  signalsAll:   (params)      => http.get('/api/agent/admin/signals', { params }).then(r => r.data),
  // 稿件审核：机器先核对过硬指标，这里只剩人要看的部分
  drafts:       (status, page) => http.get('/api/agent/admin/drafts', { params: { status, page } }).then(r => r.data),
  approveDraft: (id, note)    => http.post(`/api/agent/admin/drafts/${id}/approve`, { note }).then(r => r.data),
  rejectDraft:  (id, reason, tag) => http.post(`/api/agent/admin/drafts/${id}/reject`, { reason, tag }).then(r => r.data),
  // 事后回看：给分 + 一句建议。不影响博主已经发出去的东西 ——
  // 2026-09-18 起机器过了就直接能发，运营这一步从「放行」变成「回看」
  reviewedDraft: (id, rating, advice) =>
    http.post(`/api/agent/admin/drafts/${id}/reviewed`, { rating, advice }).then(r => r.data),
  // ── 人工活动（运营建、公开招募；挑人和审稿是人工的）──
  manualActs:   (params)      => http.get('/api/agent/admin/manual-activities', { params }).then(r => r.data),
  manualAct:    (id)          => http.get(`/api/agent/admin/manual-activities/${id}`).then(r => r.data),
  createManualAct: (d)        => http.post('/api/agent/admin/manual-activities', d).then(r => r.data),
  updateManualAct: (id, d)    => http.put(`/api/agent/admin/manual-activities/${id}`, d).then(r => r.data),
  // 确认选中：选中的人助理马上去约时间（会真的发消息和通知给博主）
  selectManual: (id, phones)  => http.post(`/api/agent/admin/manual-activities/${id}/select`, { phones }).then(r => r.data),
  nudgeManual:  (id, phones)  => http.post(`/api/agent/admin/manual-activities/${id}/nudge`, { phones }).then(r => r.data),
  closeManualAct: (id)        => http.post(`/api/agent/admin/manual-activities/${id}/close`).then(r => r.data),
  manualQuota:  (merchantId, delta, note) => http.post(`/api/agent/admin/shops/${merchantId}/manual-quota`, { delta, note }).then(r => r.data),
  rateDeal:     (dealId, d)   => http.post(`/api/agent/admin/deals/${dealId}/rate`, d).then(r => r.data),
};

// 博主端跟 agent 聊天（走博主 token）
export const agentBloggerApi = {
  conversations: ()        => bhttp.get('/api/agent/my/conversations').then(r => r.data),
  messages:      (id)      => bhttp.get(`/api/agent/my/conversations/${id}/messages`).then(r => r.data),
  say:           (id, text, images) => bhttp.post(`/api/agent/my/conversations/${id}/say`, { text, images }).then(r => r.data),
  // 博主主动接活：不填表单，直接开一条跟助理的对话
  interest:      (activityId) => bhttp.post('/api/agent/my/interest', { activityId }).then(r => r.data),
  // Instagram 绑定：只读他的公开资料（粉丝数、互动率），不能发东西也看不到私信
  igStatus:      ()        => bhttp.get('/api/agent/my/ig/status').then(r => r.data),
  igAuthUrl:     ()        => bhttp.get('/api/agent/my/ig/auth-url').then(r => r.data),
  igUnlink:      ()        => bhttp.post('/api/agent/my/ig/unlink').then(r => r.data),
};

// 商家端（凭活动分享 token，不登录）
export const agentMerchantApi = {
  get:      (token)         => axios.get(`${BASE}/api/agent/m/${token}`).then(r => r.data),
  saveTerms:(token, d)      => axios.put(`${BASE}/api/agent/m/${token}/terms`, d).then(r => r.data),
  messages: (token)         => axios.get(`${BASE}/api/agent/m/${token}/messages`).then(r => r.data),
  say:      (token, text)   => axios.post(`${BASE}/api/agent/m/${token}/say`, { text }).then(r => r.data),
  approve:  (token, dealId, approved) => axios.post(`${BASE}/api/agent/m/${token}/deals/${dealId}/approve`, { approved }).then(r => r.data),
  rate:     (token, dealId, d) => axios.post(`${BASE}/api/agent/m/${token}/deals/${dealId}/rate`, d).then(r => r.data),
  // 店员点「博主到店了」：博主那边立刻收到催稿提醒
  arrived:  (token, dealId)   => axios.post(`${BASE}/api/agent/m/${token}/deals/${dealId}/arrived`).then(r => r.data),
  // 某一单相关的往来（只读）：商家在预约详情里看，不用自己翻聊天
  dealMessages: (token, dealId) => axios.get(`${BASE}/api/agent/m/${token}/deals/${dealId}/messages`).then(r => r.data),
};

// IM WebSocket 地址
export const imUrl = () => {
  const base = process.env.NODE_ENV === 'development'
    ? (process.env.REACT_APP_API_URL || 'http://localhost:4001')
    : window.location.origin;
  return base.replace(/^http/, 'ws') + '/im';
};

// 扫码落地页（公开，无鉴权）：扫码的人换自己的图
export const scanApi = {
  uploadImage: async (articleId, file) => {
    const form = new FormData();
    form.append('file', await shrinkIfHuge(file));
    return axios
      .post(`${BASE}/api/scan/content/${articleId}/image`, form, { timeout: UPLOAD_TIMEOUT })
      .then(r => r.data.url);
  },
};

// 通用码扫码地址（首页展示二维码用）
export const universalScanUrl = () => `${
  process.env.NODE_ENV === 'development'
    ? (process.env.REACT_APP_API_URL || 'http://localhost:4001')
    : window.location.origin
}/api/scan/universal`;

const SCAN_BASE = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_API_URL || 'http://localhost:4001')
  : window.location.origin;

export const scanUrl = {
  campaign:       (id) => `${SCAN_BASE}/api/scan/campaign/${id}`,
  merchantBlogger:(id) => `${SCAN_BASE}/api/scan/merchant/${id}/blogger`,
  campaignGoogle: (id) => `${SCAN_BASE}/api/scan/campaign/${id}?channel=google`,
  article:        (id) => `${SCAN_BASE}/api/scan/article/${id}`,
  articleGoogle:  (id) => `${SCAN_BASE}/api/scan/article/${id}?channel=google`,
};
